// Shell-level fresh-achievements queue.
//
// Any handler (today: the user-bootstrap call; later: any endpoint that returns
// `fresh_achievements`) can `publish` newly-unlocked rows here. A single
// `FreshAchievementsToastHost` mounted in the workspace shell renders one
// toast at a time off the FIFO queue.
//
// Achievements that ride on the existing AdsActivityCard sparkle flow do NOT
// need to go through this queue — they are surfaced inline. This queue is for
// awards that happen *outside* the activity card's render path (first-login,
// future bootstrap awards, etc.).
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  dismissFromFreshAchievementsQueue,
  publishToFreshAchievementsQueue,
  type FreshAchievement,
} from "@/lib/freshAchievementsQueue";

export type { FreshAchievement };

export type FreshAchievementsContextValue = {
  queue: FreshAchievement[];
  publish: (rows: FreshAchievement[] | FreshAchievement) => void;
  dismiss: (id: string) => void;
  /** Stable record of every achievement that has been observed in this session. Used to suppress duplicate toasts. */
  observedIds: Set<string>;
};

const FreshAchievementsContext = createContext<FreshAchievementsContextValue | null>(null);

export function FreshAchievementsProvider(props: { children: ReactNode }) {
  const [queue, setQueue] = useState<FreshAchievement[]>([]);
  const observedRef = useRef<Set<string>>(new Set());

  const publish = useCallback((rows: FreshAchievement[] | FreshAchievement) => {
    const list = Array.isArray(rows) ? rows : [rows];
    if (list.length === 0) return;

    setQueue((prev) => publishToFreshAchievementsQueue(prev, list, observedRef.current));
  }, []);

  const dismiss = useCallback((id: string) => {
    setQueue((prev) => dismissFromFreshAchievementsQueue(prev, id));
  }, []);

  const value = useMemo<FreshAchievementsContextValue>(
    () => ({ queue, publish, dismiss, observedIds: observedRef.current }),
    [queue, publish, dismiss],
  );

  return <FreshAchievementsContext.Provider value={value}>{props.children}</FreshAchievementsContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFreshAchievements(): FreshAchievementsContextValue {
  const ctx = useContext(FreshAchievementsContext);
  if (!ctx) {
    throw new Error("useFreshAchievements must be used within FreshAchievementsProvider");
  }
  return ctx;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFreshAchievementsOptional(): FreshAchievementsContextValue | null {
  return useContext(FreshAchievementsContext);
}
